/**
 * RequireRole — Component wrapper that only renders children if user has minimum role.
 * Checks both global role AND BU-level role (buRole).
 * Users with buRole "admin" or "superuser" get ADMIN-equivalent access.
 */

import type { ReactNode } from "react";
import { Container, Alert } from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../models/types";

const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.SUPERADMIN]: 4,
  [UserRole.ADMIN]: 3,
  [UserRole.USER]: 2,
  [UserRole.GUEST]: 1,
};

interface Props {
  minRole: UserRole;
  children: ReactNode;
}

export function RequireRole({ minRole, children }: Props) {
  const { userRole, user } = useAuth();

  let userLevel = userRole ? ROLE_HIERARCHY[userRole] ?? 0 : 0;

  // BU-level role elevates access: buRole "admin" or "superuser" → ADMIN equivalent
  if (user?.buRole === "admin" || user?.buRole === "superuser") {
    userLevel = Math.max(userLevel, ROLE_HIERARCHY[UserRole.ADMIN]);
  }

  const requiredLevel = ROLE_HIERARCHY[minRole] ?? 0;

  if (userLevel < requiredLevel) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You need {minRole} role or higher to access this page.</p>
        </Alert>
      </Container>
    );
  }

  return <>{children}</>;
}
